// Hero.tsx
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './Hero.css';
import Navbar from './Navbar';
import {
  GraduationCap,
  Brain,
  TrendingUp,
  Award,
  Users,
  BookOpen,
  Zap,
  Sparkles,
  CheckCircle2,
  Star,
  ArrowRight,
  X,
} from 'lucide-react';
import {
  FaFacebookF,
  FaInstagram,
  FaLinkedinIn,
  FaSnapchatGhost,
  FaTelegramPlane,
  FaTiktok,
  FaWhatsapp,
  FaYoutube,
} from 'react-icons/fa';
import { getBanners, Banner } from '../../services/bannerService';
// Import Swiper React components
import { Swiper, SwiperSlide } from 'swiper/react';
// Import Swiper styles
import 'swiper/css';
import 'swiper/css/pagination';
import { Autoplay, Pagination } from 'swiper/modules';

const Hero = () => {
  const navigate = useNavigate();
  const [statsVisible, setStatsVisible] = useState(false);
  const [comingSoonAudience, setComingSoonAudience] = useState<string | null>(null);
  const [banners, setBanners] = useState<Banner[]>([]);
  const [isInstallModalOpen, setIsInstallModalOpen] = useState(true);
  const playStoreUrl = 'https://play.google.com/store/apps/details?id=ai.zaptick';

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setStatsVisible(true);
          }
        });
      },
      { threshold: 0.3 }
    );

    const statsSection = document.querySelector('.stats-section');
    if (statsSection) {
      observer.observe(statsSection);
    }

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const fetchBanners = async () => {
      try {
        const data = await getBanners();
        setBanners(data);
      } catch (error) {
        console.error("Failed to fetch banners", error);
      }
    };
    fetchBanners();
  }, []);

  const handleLoginClick = () => {
    navigate('/login');
  };

  const handleComingSoonClick = (audience: 'Teachers' | 'Parents') => {
    setComingSoonAudience(audience);
  };

  const closeComingSoon = () => {
    setComingSoonAudience(null);
  };

  const closeInstallModal = () => {
    setIsInstallModalOpen(false);
  };

  const features = [
    {
      icon: Brain,
      title: 'AI-Powered Learning',
      description: 'Personalized study plans adapted to your learning style and pace'
    },
    {
      icon: TrendingUp,
      title: 'Grade Improvement',
      description: 'Proven 2x grade boost with adaptive mock tests and instant feedback'
    },
    {
      icon: Award,
      title: 'Expert Content',
      description: 'Created by teachers and examiners for GCSE, A-Level & IB curricula'
    },
    {
      icon: Zap,
      title: 'Instant Marking',
      description: 'Get detailed feedback on your work in seconds, not days'
    }
  ];

  const stats = [
    { value: '50K+', label: 'Active Students' },
    { value: '2x', label: 'Grade Improvement' },
    { value: '1M+', label: 'Questions Solved' },
    { value: '98%', label: 'Success Rate' }
  ];

  const testimonials = [
    {
      name: 'Sarah Johnson',
      role: 'A-Level Student',
      content: 'Went from a C to an A* in Chemistry! The AI tutor explained concepts in ways my teachers couldn\'t.',
      rating: 5
    },
    {
      name: 'Michael Chen',
      role: 'GCSE Student',
      content: 'The mock tests are incredibly realistic. I felt so prepared for my actual exams!',
      rating: 5
    },
    {
      name: 'Emma Williams',
      role: 'Parent',
      content: 'Finally, a platform that keeps me updated on my child\'s progress. Worth every penny!',
      rating: 5
    }
  ];

  const socialLinks = [
    {
      label: 'WhatsApp',
      href: 'https://whatsapp.com/channel/0029Vb6mAAMIt5rx37l9OX1w',
      icon: FaWhatsapp,
      className: 'whatsapp',
    },
    {
      label: 'Instagram',
      href: 'https://www.instagram.com/zaptick.ai?igsh=ejJ4emFzZDFwNHB0',
      icon: FaInstagram,
      className: 'instagram',
    },
    {
      label: 'Facebook',
      href: 'https://www.facebook.com/share/1AaHStfCLF/?mibextid=wwXIfr',
      icon: FaFacebookF,
      className: 'facebook',
    },
    {
      label: 'TikTok',
      href: 'https://www.tiktok.com/@zaptick1?_t=ZS-8wtTcZJAdfB&_r=1',
      icon: FaTiktok,
      className: 'tiktok',
    },
    {
      label: 'YouTube',
      href: 'https://youtube.com/@zaptick?si=Dj2CYG-5TzKSe3J0',
      icon: FaYoutube,
      className: 'youtube',
    },
    {
      label: 'Telegram',
      href: 'https://t.me/zaptickai',
      icon: FaTelegramPlane,
      className: 'telegram',
    },
    {
      label: 'Snapchat',
      href: 'https://www.snapchat.com/@zaptickai',
      icon: FaSnapchatGhost,
      className: 'snapchat',
    },
    {
      label: 'LinkedIn',
      href: 'https://www.linkedin.com/company/zaptick-ai/',
      icon: FaLinkedinIn,
      className: 'linkedin',
    },
  ];

  return (
    <>
      <Navbar />

      {isInstallModalOpen && (
        <div className="install-app-modal-overlay" role="dialog" aria-modal="true" aria-labelledby="install-app-title">
          <div className="install-app-modal">
            <button
              type="button"
              className="install-app-modal-close"
              onClick={closeInstallModal}
              aria-label="Close install app prompt"
            >
              <X size={18} />
            </button>
            <div className="install-app-icon">
              <img src="/logo512.png" alt="ZapTick AI app logo" />
            </div>
            <p className="install-app-kicker">ZapTick AI App</p>
            <h2 id="install-app-title">Install ZapTick AI</h2>
            <p className="install-app-copy">
              Learn faster with mock tests, AI tutor support, and progress tracking on the mobile app.
            </p>
            <a
              className="install-app-button"
              href={playStoreUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              Open Play Store
              <ArrowRight size={18} />
            </a>
          </div>
        </div>
      )}

      {/* Animated Background */}
      <div className="hero-background">
        <div className="gradient-orb orb-1"></div>
        <div className="gradient-orb orb-2"></div>
        <div className="gradient-orb orb-3"></div>
      </div>

      {/* Hero Section */}
      <section className="hero">
        <div className="hero-content">

          {/* Banners Carousel */}
          {banners.length > 0 && (
            <div
              className="w-full mb-8 rounded-2xl overflow-hidden fade-in-up"
              style={{
                border: '1px solid var(--border-2)',
                boxShadow: 'var(--shadow-1)',
                borderRadius: '20px'
              }}
            >
              <Swiper
                spaceBetween={30}
                centeredSlides={true}
                autoplay={{
                  delay: 3000,
                  disableOnInteraction: false,
                }}
                pagination={{
                  clickable: true,
                }}
                modules={[Autoplay, Pagination]}
                className="mySwiper"
                style={{ borderRadius: '16px' }}
              >
                {banners.map((banner) => (
                  <SwiperSlide key={banner._id}>
                    <img
                      src={banner.imageUrl}
                      alt="Banner"
                      style={{
                        width: '100%',
                        height: '100%',
                        maxHeight: '450px',
                        objectFit: 'contain',
                        display: 'block',
                        margin: '0 auto',
                      }}
                    />
                  </SwiperSlide>
                ))}
              </Swiper>
            </div>
          )}

          {/* Main Headline */}
          <div className="hero-headline">
            <div className="sparkle-badge fade-in-up">
              <Sparkles size={16} />
              <span>Built for GCSE, A-Level, and IB learners</span>
            </div>
            <h1 className="gradient-text fade-in-up delay-1">
              AI Study Platform for Better Exam Scores
            </h1>
            <p className="hero-subtitle fade-in-up delay-2">
              Personalized tutoring, exam-style mock practice, and instant feedback in one platform,
              helping students study consistently and improve faster.
            </p>
          </div>

          {/* CTA Buttons */}
          <div className="cta-section fade-in-up delay-3">
            <div className="cta-grid">
              <div className="cta-card glass-card">
                <div className="cta-icon">
                  <GraduationCap size={32} />
                </div>
                <h3>For Teachers</h3>
                <p>Empower your classroom with AI-driven insights</p>
                <button
                  className="cta-button secondary"
                  onClick={() => handleComingSoonClick('Teachers')}
                >
                  Learn More
                  <ArrowRight size={18} />
                </button>
              </div>

              <div className="cta-card glass-card primary-card">
                <div className="cta-icon primary">
                  <Users size={32} />
                </div>
                <h3>For Students</h3>
                <p>Start your journey to academic excellence</p>
                <button className="cta-button primary" onClick={handleLoginClick}>
                  Get Started Free
                  <ArrowRight size={18} />
                </button>
              </div>

              <div className="cta-card glass-card">
                <div className="cta-icon">
                  <BookOpen size={32} />
                </div>
                <h3>For Parents</h3>
                <p>Track progress with detailed analytics</p>

                <button
                  className="cta-button secondary"
                  onClick={() => navigate('/parent-register')}
                >
                  Register as Parent
                  <ArrowRight size={18} />
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Social Links Section */}
      <section className="social-links-section">
        <div className="social-links-content fade-in-up">
          <div className="section-header social-header">
            <span className="social-kicker">Connect with ZapTick AI</span>
            <h2>Follow Us for Updates</h2>
          </div>
          <div className="social-links-grid" aria-label="ZapTick AI social links">
            {socialLinks.map((social) => (
              <a
                key={social.label}
                className={`social-link-card ${social.className}`}
                href={social.href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={`Open ZapTick AI on ${social.label}`}
              >
                <span className="social-link-icon">
                  <social.icon aria-hidden="true" />
                </span>
                <span>{social.label}</span>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="features-section">
        <div className="section-header fade-in-up">
          <h2>Why Students Love ZapTick AI</h2>
        </div>
        <div className="features-grid">
          {features.map((feature, index) => (
            <div
              key={index}
              className="feature-card glass-card fade-in-up"
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              <div className="feature-icon">
                <feature.icon size={28} />
              </div>
              <h3>{feature.title}</h3>
              <p>{feature.description}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Stats Section */}
      <section className="stats-section">
        <div className="stats-grid">
          {stats.map((stat, index) => (
            <div
              key={index}
              className={`stat-card ${statsVisible ? 'count-up' : ''}`}
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              <div className="stat-value">{stat.value}</div>
              <div className="stat-label">{stat.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="testimonials-section">
        <div className="section-header fade-in-up">
          <h2>Trusted by Thousands of Students</h2>
        </div>
        <div className="testimonials-grid">
          {testimonials.map((testimonial, index) => (
            <div
              key={index}
              className="testimonial-card glass-card fade-in-up"
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              <div className="stars">
                {[...Array(testimonial.rating)].map((_, i) => (
                  <Star key={i} size={16} fill="currentColor" />
                ))}
              </div>
              <p className="testimonial-content">"{testimonial.content}"</p>
              <div className="testimonial-author">
                <div className="author-avatar">
                  {testimonial.name.charAt(0)}
                </div>
                <div>
                  <div className="author-name">{testimonial.name}</div>
                  <div className="author-role">{testimonial.role}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="final-cta-section">
        <div className="final-cta-content glass-card fade-in-up">
          <h2>Ready to Transform Your Grades?</h2>
          <button className="cta-button primary large" onClick={handleLoginClick}>
            Start Learning for Free
            <ArrowRight size={20} />
          </button>
          <div className="cta-features">
            <div className="cta-feature">
              <CheckCircle2 size={16} />
              <span>Free trial included</span>
            </div>
            <div className="cta-feature">
              <CheckCircle2 size={16} />
              <span>Cancel anytime</span>
            </div>
          </div>
        </div>
      </section>

      {comingSoonAudience && (
        <div className="coming-soon-overlay" onClick={closeComingSoon}>
          <div className="coming-soon-modal glass-card" onClick={(e) => e.stopPropagation()}>
            <h3>{comingSoonAudience} experience is coming soon</h3>
            <p>
              We&apos;re polishing dedicated features for {comingSoonAudience.toLowerCase()} and will launch them shortly.
              Leave your email inside the app to get early access.
            </p>
            <button className="cta-button primary" onClick={closeComingSoon}>
              Got it
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default Hero;
